import * as React from "react";
import { observable } from "mobx";
import { observer, inject } from "mobx-react";
import { hot } from "react-hot-loader";
import { ToastContainer } from "react-toastify";
import { IAuthStore, oauthLogInWithAccountLinkRoute } from "Stores/Auth/types";
import { withRouter, RouteComponentProps } from "react-router";
import Routes from "./Components/Common/Routes/Routes";
import PostBackFormSubmittion from "@src/Tools/PostBackFormSubmittion";
import { IValuesStore } from "./Stores/Values/types";
import LinearProgress from "./Components/Common/LinearProgress/LinearProgress";
import AppPreloader from "./Components/Common/AppPreloader/AppPreloader";
import qs from "query-string";
import { STAppWrapper } from "./styles";
import { IThemesStore } from "./Stores/Themes/types";
import { RouterStore } from "mobx-react-router";
import { ThemeProvider } from "styled-components";
import { CssBaseline } from "@material-ui/core";
import Cookies from "js-cookie";

interface IProps extends RouteComponentProps {
  auth?: IAuthStore;
  values?: IValuesStore;
  themes?: IThemesStore;
  routing?: RouterStore;
}

interface IOutputParams {
  ssopostback?: string;
  backurl?: string;
  mode?: string;
  forcesignout?: string;
  afterauthredirecturl?: string;
}

@inject("auth", "values", "themes", "routing")
@observer
class App extends React.Component<IProps> {
  @observable isLoading = true;

  checkAuth = async () => {
    try {
      const tenantParam = new URLSearchParams(this.props.location.search).get("tenant");

      const authResult: boolean = await this.props.auth.checkAuth(tenantParam);
      const queryString: IOutputParams = (qs.parse(this.props.location.search) as unknown) as IOutputParams;
      const queryLowerCased: IOutputParams = {};
      Object.entries(queryString).map(([key, value]) => {
        return (queryLowerCased[key.toLowerCase()] = value);
      });

      if (authResult && queryLowerCased.ssopostback) {
        const token: string = await this.props.auth.getToken();
        if (queryLowerCased.mode && queryLowerCased.mode.toLowerCase() === "get") {
          const url = queryLowerCased.ssopostback;
          const updatedUrl = url + (url.split("?")[1] ? "&" : "?") + `token=${token}` + this.props.location.hash;
          return (window.location.href = updatedUrl);
        }
        PostBackFormSubmittion(token, queryLowerCased.ssopostback);
      } else if (authResult && queryLowerCased.backurl) {
        await this.props.auth.getUserDetails();
        this.props.auth.setBackUrl(queryLowerCased.backurl);
        this.props.history.replace(this.props.location.pathname);
      } else if (queryLowerCased.afterauthredirecturl) {
        if (authResult) {
          window.location.href = queryLowerCased.afterauthredirecturl;
        } else {
          this.props.auth.setAfterAuthRedirectUrl(queryLowerCased.afterauthredirecturl);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  onPageReloadRequested = () => {
    console.log("Page reload requested");
    window.location.reload();
  };

  setHTMLLang = () => {
    const lang: string = this.props.values.language;
    document.documentElement.lang = lang;
  };

  loadTheme = async (tenant?: string) => {
    await this.props.themes.getCurrentTheme(this.props.values.themeTypes.Application, tenant);
  };

  async componentDidMount() {
    try {
      const tenantParam = new URLSearchParams(this.props.location.search).get("tenant");
      const { versionTag, versionHash, gitBranch, applicationName } = await this.props.values.getValues(tenantParam);
      await this.loadTheme(tenantParam);
      await this.checkAuth();

      document.getElementsByTagName("META").namedItem("build-tag")?.setAttribute("content", versionTag);
      document.getElementsByTagName("META").namedItem("build-sha")?.setAttribute("content", versionHash);
      document.getElementsByTagName("META").namedItem("git-branch")?.setAttribute("content", gitBranch);
      document.getElementsByTagName("META").namedItem("app-name")?.setAttribute("content", applicationName);

      this.isLoading = false;

      this.setHTMLLang();

      window.addEventListener("requestPageReload", this.onPageReloadRequested);
      this.checkIsExistSessionToken();
    } catch (error) {
      console.error(error);
    }
  }

  checkIsExistSessionToken = () => {
    const intervalId = setInterval(() => {
      const sessionTokenCookie = Cookies.get("SessionToken");
      if (!sessionTokenCookie) {
        return;
      }
      clearInterval(intervalId);
      if (!this.props.auth.user) {
        const queryString: IOutputParams = (qs.parse(this.props.location.search) as unknown) as IOutputParams;
        const queryLowerCased: IOutputParams = {};
        Object.entries(queryString).map(([key, value]) => {
          if (key.toLowerCase() === "forcesignout") {
            return;
          }
          return (queryLowerCased[key.toLowerCase()] = value);
        });
        let updatedUrl = `${window.location.origin}?`;
        for (var prop in queryLowerCased) {
          updatedUrl = `${updatedUrl}${prop}=${queryLowerCased[prop]}&`;
        }
        this.props.routing.history.push(updatedUrl);
        this.onPageReloadRequested();
      }
    }, 1000);
  };

  componentWillUnmount() {
    window.removeEventListener("requestPageReload", this.onPageReloadRequested);
  }

  renderContent() {
    if (this.isLoading) {
      return <AppPreloader />;
    } else if (this.props.auth.isAuthorized && this.props.location.search.match("ssopostback")) {
      return <AppPreloader />;
    }
    return (
      <STAppWrapper>
        <LinearProgress />
        <Routes match={this.props.match} />
      </STAppWrapper>
    );
  }

  render() {
    return (
      <React.Fragment>
        <ThemeProvider theme={this.props.themes.application || {}}>
          <CssBaseline />
          <div>
            {this.renderContent()}
            <ToastContainer draggable={false} />
          </div>
        </ThemeProvider>
      </React.Fragment>
    );
  }
}

export default hot(module)(withRouter(App));
